import { Link } from 'react-router-dom';
import { Search, Menu } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle
} from './ui/sheet';

export default function Header({ searchValue, onSearchChange }) {
  const isControlled = typeof onSearchChange === 'function';
  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm" dir="rtl">
      <div className="container mx-auto px-4">
        {/* Top Bar */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_d66cea65-6b3a-4085-9d05-44a364709b5b/artifacts/fug4q088_OroodhLogo%281%29.png" 
              alt="صفقة" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Search Bar - Center */}
          <div className="flex-1 max-w-2xl mx-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="search"
                placeholder="ابحث عن عروض ومنتجات..."
                className="w-full pr-10 pl-4 py-2.5 bg-gray-50 border-gray-200 rounded-full focus:ring-2 focus:ring-primary focus:border-transparent text-right"
                dir="rtl"
                value={isControlled ? searchValue : undefined}
                onChange={isControlled ? (event) => onSearchChange(event.target.value) : undefined}
              />
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            <Link to="/request" className="hidden md:inline-flex">
              <Button className="rounded-full px-5 bg-primary text-white hover:bg-primary/90 shadow-sm">
                اطلب كوبون تخفيض
              </Button>
            </Link>
            <Link to="/request" className="md:hidden">
              <Button variant="outline" className="rounded-full">
                اطلب كوبون
              </Button>
            </Link>
            {/* Mobile Menu Button */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden rounded-full h-10 w-10 hover:bg-gray-100"
                  aria-label="القائمة"
                >
                  <Menu className="h-5 w-5 text-gray-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="p-0">
                <div className="h-full flex flex-col" dir="rtl">
                  <div className="px-6 py-5 border-b border-gray-100">
                    <div className="flex items-center justify-end">
                      <SheetTitle className="text-right text-base font-bold">القائمة</SheetTitle>
                    </div>
                  </div>
                  <div className="flex items-center justify-center py-6">
                    <SheetClose asChild>
                      <Link to="/" className="flex items-center">
                        <img
                          src="https://customer-assets.emergentagent.com/job_d66cea65-6b3a-4085-9d05-44a364709b5b/artifacts/fug4q088_OroodhLogo%281%29.png"
                          alt="صفقة"
                          className="h-20 w-auto"
                        />
                      </Link>
                    </SheetClose>
                  </div>
                  <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6">
                    <SheetClose asChild>
                      <Link to="/" className="w-full">
                        <Button variant="ghost" className="w-full rounded-full bg-gray-50 hover:bg-gray-100 text-gray-800">
                          جميع العروض
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/category/amazon" className="w-full">
                        <Button variant="ghost" className="w-full rounded-full bg-gray-50 hover:bg-gray-100 text-gray-800">
                          أمازون
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/category/noon" className="w-full">
                        <Button variant="ghost" className="w-full rounded-full bg-gray-50 hover:bg-gray-100 text-gray-800">
                          نون
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/category/namshi" className="w-full">
                        <Button variant="ghost" className="w-full rounded-full bg-gray-50 hover:bg-gray-100 text-gray-800">
                          نمشي
                        </Button>
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/category/aliexpress" className="w-full">
                        <Button variant="ghost" className="w-full rounded-full bg-gray-50 hover:bg-gray-100 text-gray-800">
                          علي إكسبريس
                        </Button>
                      </Link>
                    </SheetClose>
                  </div>
                  <div className="px-6 pb-6">
                    <SheetClose asChild>
                      <Link to="/request" className="w-full">
                        <Button className="w-full rounded-full bg-primary text-white hover:bg-primary/90">
                          اطلب كوبون تخفيض
                        </Button>
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Categories Bar - Hidden on mobile (will be in carousel on home page) */}
        <div className="hidden md:flex items-center justify-center border-t border-gray-100 py-3">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              جميع العروض
            </Link>
            <Link
              to="/category/amazon"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              أمازون
            </Link>
            <Link
              to="/category/noon"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              نون
            </Link>
            <Link
              to="/category/namshi"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              نمشي
            </Link>
            <Link
              to="/category/aliexpress"
              className="text-sm font-medium text-gray-700 hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50"
            >
              علي إكسبريس
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
